import React, { useState, useEffect, useRef } from 'react';
import { SketchPicker } from 'react-color';
import PropTypes from 'prop-types';
import { getAuth } from 'firebase/auth';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    RadioGroup,
    FormControlLabel,
    Checkbox,
    Radio,
    TextField,
    Select,
    MenuItem,
    Box,
    Typography,
    FormControl,
    Divider,
    Popover
} from '@mui/material';
import HelpIcon from './HelpIcon';
import { useInitialConfig } from '../utils/useInitialConfig';

/*
 * The six lookup tables moved to utils/timeEventTables.js on 2026-10-24 so the payload builder
 * can share them -- `signs.indexOf(x) + 1` is what the backend receives, so the dropdown this
 * renders and the index the builder sends must read ONE array or an inserted entry shifts them
 * apart silently.
 */
import {
    planets, signs, declinationAspects, declinationAspectMap, fixedStars, nakshatras,
} from '../utils/timeEventTables';

/**
 * The top-level prefill keys this dialog's applier reads.
 *
 * <p>⚠ EXPORTED SO AN UNKNOWN FIELD CAN BE REFUSED RATHER THAN SILENTLY DROPPED. A prefill
 * naming a field that does not exist would otherwise open the dialog, change nothing and report
 * success — which for a Copilot is the "a control that lies" failure, in the one place the
 * caller has no way to notice it. Same precedent as `ctsDirection`, which refuses an unknown
 * name with the valid set listed rather than quietly running the default.
 *
 * <p>Nested keys are deliberately NOT listed: `mergeInto` already ignores sub-keys it does not
 * know, and enumerating them here would be a second declaration of every form's shape.
 *
 * <p>`prefillFields.test.js` reads the applier and fails if the two drift apart.
 */
export const PREFILL_FIELDS = Object.freeze([
  'timeOption', 'formValues', 'colors',
]);

const TimeAndEventStrategyDialog = ({ open, onClose, onRun, initialConfig = null }) => {
    const [colors, setColors] = useState({
        harmonic: '#E67C73',
        planet_entering_sign: '#4285F4',
        entering_nakshatra: '#7986CB',
        aspect: '#34A853',
        declination_aspect: '#F4B400',
        zero_declination: '#8E67FD',
        friendly_sign: '#616161',
        inimical_sign: '#FF8A65',
        galactic_center: '#9E69AF',
        rising: '#00ACC1',
        declination_more_than_sun: '#F06292',
        solarOrLunarEclipse: '#A1887F',
        conjunct_star: '#B39DDB'
    });
    const [pickerAnchorEl, setPickerAnchorEl] = useState(null);
    const [currentPicker, setCurrentPicker] = useState(null);
    const colorsRef = useRef(colors);

    const handleColorClick = (event, studyType) => {
        setPickerAnchorEl(event.currentTarget);
        setCurrentPicker(studyType);
    };

    const handleColorClose = () => {
        setPickerAnchorEl(null);
        setCurrentPicker(null);
    };

    const handleColorChange = (color) => {
        if (currentPicker) {
            setColors(prevColors => ({
                ...prevColors,
                [currentPicker]: color.hex
            }));
            colorsRef.current[currentPicker] = color.hex;
        }
    };

    const isPickerOpen = Boolean(pickerAnchorEl);
    const [timeOption, setTimeOption] = useState('before');
    const [highlightedRow, setHighlightedRow] = useState(null);

    useEffect(() => {
        colorsRef.current = colors;
    }, [colors]);

    const [formValues, setFormValues] = useState({
        time_before: '10',
        time_after: '10',
        time_around_before: '10',
        time_around_after: '10',
        harmonic: '12',
        location: '0',
        harmonic_planet: 'Sun',
        entering_sign_planet: 'Mercury',
        entering_nakshatra_planet: 'Moon',
        aspect_planet1: 'Mars',
        aspect_planet2: 'Sun',
        entering_sign: 'Aries',
        entering_nakshatra: 'Ashwini',
        aspect: 0,
        declination_aspect: 'Parallel',
        declination_planet1: 'Mars',
        declination_planet2: 'Mercury',
        zero_declination_planet: 'Mercury',
        friendly_sign_planet: '',
        inimical_sign_planet: '',
        galactic_center_planet: 'Venus',
        rising_planet: '',
        setting_planet: 'Mars',
        rising_value: '',
        eclipse_type: 'Solar',
        // The extension is OFF by default. It is an additional pass over every eclipse window, and
        // a study that quietly got slower and busier the day it shipped would be a worse study.
        eclipse_track_transits: false,
        eclipse_transit_scope: 'Solar',
        conjunct_star_planet: 'Sun',
        conjunct_star: 'Hamel'
    });

    /**
     * PREFILL. `formValues` and `colors` MERGE, so naming one field leaves the rest of the form
     * alone -- a prefill that replaced them would silently clear the other thirty.
     */
      useInitialConfig(initialConfig, (cfg) => {
        if (cfg.timeOption !== undefined) setTimeOption(cfg.timeOption);
        if (cfg.formValues) setFormValues(prev => ({ ...prev, ...cfg.formValues }));
        if (cfg.colors) setColors(prev => ({ ...prev, ...cfg.colors }));
    });

    const handleRunStudy = (studyType) => {
        const studyParams = {};
        switch (studyType) {
            case 'harmonic':
                studyParams.planet = formValues.harmonic_planet;
                studyParams.harmonic = formValues.harmonic;
                studyParams.location = formValues.location;
                break;
            case 'planet_entering_sign':
                studyParams.planet = formValues.entering_sign_planet;
                studyParams.sign = signs.indexOf(formValues.entering_sign) + 1;
                break;
            case 'entering_nakshatra':
                studyParams.planet = formValues.entering_nakshatra_planet;
                studyParams.nakshatra = nakshatras.indexOf(formValues.entering_nakshatra) + 1;
                break;
            case 'aspect':
                studyParams.planet1 = formValues.aspect_planet1;
                studyParams.planet2 = formValues.aspect_planet2;
                studyParams.aspect = formValues.aspect;
                break;
            case 'declination_aspect':
                studyParams.planet1 = formValues.declination_planet1;
                studyParams.planet2 = formValues.declination_planet2;
                studyParams.declination_aspect = declinationAspectMap[formValues.declination_aspect];
                break;
            case 'zero_declination':
                studyParams.planet = formValues.zero_declination_planet;
                break;
            case 'friendly_sign':
                studyParams.planet = formValues.friendly_sign_planet;
                break;
            case 'inimical_sign':
                studyParams.planet = formValues.inimical_sign_planet;
                break;
            case 'galactic_center':
                studyParams.planet = formValues.galactic_center_planet;
                break;
            case 'rising':
                studyParams.planet = formValues.rising_planet;
                break;
            case 'declination_more_than_sun':
                studyParams.planet = formValues.setting_planet; // Note: state is setting_planet
                break;
            case 'solarOrLunarEclipse':
                studyParams.type = formValues.eclipse_type;
                studyParams.trackTransits = !!formValues.eclipse_track_transits;
                if (formValues.eclipse_track_transits) {
                    studyParams.transitScope = formValues.eclipse_transit_scope;
                }
                break;
            case 'conjunct_star': {
                studyParams.planet = formValues.conjunct_star_planet;
                const selectedStar = fixedStars.find(s => s.name === formValues.conjunct_star);
                if (selectedStar) {
                    studyParams.star_longitude = selectedStar.longitude;
                }
                break;
            }
            default:
                console.error(`Unknown study type: ${studyType}`);
                return;
        }

        /*
         * EMITS THE CONFIG, not a payload. The assembly moved WHOLE to
         * utils/strategyPayloadBuilders.js on 2026-10-24: it used to be split between the switch
         * above and App's handler, which is precisely why a headless run had to reimplement one
         * half. App builds from this; `strategy.run` builds from the same function.
         */
        const config = { studyType, timeOption, formValues, colors: colorsRef.current };

        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
            alert('You are not logged in. Please log in again.');
            return;
        }

        console.log('Time and Event config being sent:', JSON.stringify(config, null, 2));
        setHighlightedRow(studyType);
        onRun(config);
        user.getIdToken(true).catch(error => {
            console.error('Error refreshing token:', error);
            alert('Could not refresh authentication token. Please log in again.');
        });
    };

    const renderRow = (controls, rowId, disabled) => {
        let isTimeInputInvalid = false;
        if (timeOption === 'before' && !formValues.time_before) {
            isTimeInputInvalid = true;
        } else if (timeOption === 'after' && !formValues.time_after) {
            isTimeInputInvalid = true;
        } else if (timeOption === 'around' && (!formValues.time_around_before || !formValues.time_around_after)) {
            isTimeInputInvalid = true;
        }
        const isHighlighted = highlightedRow === rowId;
        return (
            <Box sx={{
                border: isHighlighted ? '2px solid #1976d2' : '1px solid #ccc',
                borderRadius: '4px',
                p: 1,
                mb: 1,
                backgroundColor: isHighlighted ? '#e3f2fd' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {controls.map((control, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                            {control.component}
                        </Box>
                    ))}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                        sx={{
                            width: 24,
                            height: 24,
                            backgroundColor: colors[rowId],
                            cursor: 'pointer',
                            border: '1px solid #ccc',
                            borderRadius: '4px'
                        }}
                        onClick={(e) => handleColorClick(e, rowId)}
                    />
                    <Button variant="contained" onClick={() => handleRunStudy(rowId)} disabled={disabled || isTimeInputInvalid}>RUN</Button>
                </Box>
            </Box>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
            <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{flexGrow: 1, textAlign: 'center', paddingLeft: '48px'}}>Time And Event Strategy</span>
        <HelpIcon helpKey="timeAndEventStrategyDialog" />
      </DialogTitle>
            <DialogContent>
                {/* Time Window Selection */}
                <Box sx={{ border: '2px solid #999', borderRadius: '4px', p: 1, my: 1 }}>
                    <RadioGroup row value={timeOption} onChange={(e) => setTimeOption(e.target.value)} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FormControlLabel value="before" control={<Radio />} label="From" />
                            <TextField type="number" size="small" sx={{ width: 80, mx: 1 }} value={formValues.time_before} onChange={(e) => setFormValues(v => ({...v, time_before: e.target.value}))} />
                            <Typography sx={{ fontSize: '0.875rem' }}>days before to the event</Typography>
                        </Box>

                        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(0, 0, 0, 0.4)' }} />

                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FormControlLabel value="after" control={<Radio />} label="From event to" />
                            <TextField type="number" size="small" sx={{ width: 80, mx: 1 }} value={formValues.time_after} onChange={(e) => setFormValues(v => ({...v, time_after: e.target.value}))} />
                            <Typography sx={{ fontSize: '0.875rem' }}>days after</Typography>
                        </Box>

                        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(0, 0, 0, 0.4)' }} />

                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FormControlLabel value="around" control={<Radio />} label="From" />
                            <TextField type="number" size="small" sx={{ width: 80, mx: 1 }} value={formValues.time_around_before} onChange={(e) => setFormValues(v => ({...v, time_around_before: e.target.value}))} />
                            <Typography sx={{ mr: 1, fontSize: '0.875rem' }}>days before to</Typography>
                            <TextField type="number" size="small" sx={{ width: 80, mx: 1 }} value={formValues.time_around_after} onChange={(e) => setFormValues(v => ({...v, time_around_after: e.target.value}))} />
                            <Typography sx={{ fontSize: '0.875rem' }}>days after</Typography>
                        </Box>
                    </RadioGroup>
                </Box>

                {/* Event Rows */}
                <Box sx={{ maxWidth: '55%', margin: '0 auto' }}>
                    {renderRow([
                        { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                        { component: (
                            <FormControl size="small" sx={{ width: 120 }}>
                                <Select value={formValues.harmonic_planet} onChange={(e) => setFormValues(v => ({...v, harmonic_planet: e.target.value}))}>
                                    {planets.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                                </Select>
                            </FormControl>
                        )},
                        { component: <Typography sx={{ fontSize: '0.875rem' }}>Harmonic</Typography> },
                        { component: <TextField 
                            type="number" 
                            size="small" 
                            sx={{ width: 67 }} 
                            value={formValues.harmonic} 
                            onChange={(e) => {
                                const newHarmonic = e.target.value;
                                setFormValues(v => ({...v, harmonic: newHarmonic}));

                                const harmonicValue = parseFloat(newHarmonic);
                                if (harmonicValue >= 1 && harmonicValue <= 180) {
                                    const maxLocation = (360 / harmonicValue) - 1;
                                    let locationValue = parseFloat(formValues.location);
                                    if (!isNaN(locationValue) && locationValue > maxLocation) {
                                        setFormValues(v => ({...v, harmonic: newHarmonic, location: maxLocation.toFixed(2)}));
                                    }
                                }
                            }} 
                            /> 
                        },
                        { component: <Typography sx={{ fontSize: '0.875rem' }}>Location in harmonic window in degrees</Typography> },
                        { component: <TextField 
                            type="number" 
                            size="small" 
                            sx={{ width: 67 }} 
                            value={formValues.location} 
                            onChange={(e) => {
                                const newLocation = e.target.value;
                                const harmonic = parseFloat(formValues.harmonic);
                                const locationValue = parseFloat(newLocation);

                                if (newLocation === '' || isNaN(locationValue)) {
                                    setFormValues(v => ({...v, location: newLocation}));
                                    return;
                                }

                                if (harmonic >= 1 && harmonic <= 180) {
                                    const maxLocation = (360 / harmonic) - 1;
                                    if (locationValue > maxLocation) {
                                        setFormValues(v => ({...v, location: maxLocation.toFixed(2)}));
                                    } else if (locationValue < 0) {
                                        setFormValues(v => ({...v, location: '0'}));
                                    } else {
                                        setFormValues(v => ({...v, location: newLocation}));
                                    }
                                } else {
                                    setFormValues(v => ({...v, location: newLocation}));
                                }
                            }}
                            /> 
                        }
                    ], 'harmonic', !formValues.harmonic || !formValues.location)}

                {renderRow([
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.entering_sign_planet} onChange={(e) => setFormValues(v => ({...v, entering_sign_planet: e.target.value}))}>
                                {planets.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Entering Sign</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.entering_sign} onChange={(e) => setFormValues(v => ({...v, entering_sign: e.target.value}))}>
                                {signs.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )}
                ], 'planet_entering_sign', false)}

                {renderRow([
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.entering_nakshatra_planet} onChange={(e) => setFormValues(v => ({...v, entering_nakshatra_planet: e.target.value}))}>
                                {planets.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Entering Nakshatra</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.entering_nakshatra} onChange={(e) => setFormValues(v => ({...v, entering_nakshatra: e.target.value}))}>
                                {nakshatras.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )}
                ], 'entering_nakshatra')}

                {renderRow([
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.aspect_planet1} onChange={(e) => setFormValues(v => ({...v, aspect_planet1: e.target.value}))}>
                                {planets.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.aspect_planet2} onChange={(e) => setFormValues(v => ({...v, aspect_planet2: e.target.value}))}>
                                {planets.filter(p => p !== formValues.aspect_planet1).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Aspect degrees</Typography> },
                    { component: (
                        <TextField 
                            type="number" 
                            size="small" 
                            sx={{ width: 80 }} 
                            value={formValues.aspect} 
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || (val >= 0 && val <= 359)) {
                                    setFormValues(v => ({...v, aspect: val}));
                                }
                            }}
                            inputProps={{ min: 0, max: 359 }}
                        />
                    )}
                ], 'aspect', formValues.aspect_planet1 === formValues.aspect_planet2)}

                {renderRow([
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.declination_planet1} onChange={(e) => setFormValues(v => ({...v, declination_planet1: e.target.value}))}>
                                {planets.filter(p => !['Moon', 'Rahu', 'Ketu'].includes(p) && p !== 'Sun').map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                                <MenuItem key="Sun" value="Sun">Sun</MenuItem>
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.declination_planet2} onChange={(e) => setFormValues(v => ({...v, declination_planet2: e.target.value}))}>
                                {planets.filter(p => !['Moon', 'Rahu', 'Ketu'].includes(p) && p !== formValues.declination_planet1 && p !== 'Sun').map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                                <MenuItem key="Sun" value="Sun">Sun</MenuItem>
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Declination Aspect</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.declination_aspect} onChange={(e) => setFormValues(v => ({...v, declination_aspect: e.target.value}))}>
                                {declinationAspects.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )}
                ], 'declination_aspect', formValues.declination_planet1 === formValues.declination_planet2 || !formValues.declination_aspect)}

                {renderRow([
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.zero_declination_planet} onChange={(e) => setFormValues(v => ({...v, zero_declination_planet: e.target.value}))}>
                                {planets.filter(p => !['Moon', 'Rahu', 'Ketu'].includes(p)).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Zero Declination</Typography> }
                ], 'zero_declination')}

                {renderRow([
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }} disabled>
                            <Select value={formValues.friendly_sign_planet}>
                                {planets.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}></Typography> }
                ], 'friendly_sign', true)}

                {renderRow([
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }} disabled>
                            <Select value={formValues.inimical_sign_planet}>
                                {planets.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}></Typography> }
                ], 'inimical_sign', true)}

                {renderRow([
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }} disabled>
                            <Select value={formValues.rising_planet} onChange={(e) => setFormValues(v => ({...v, rising_planet: e.target.value}))}>
                                {planets.filter(p => p !== 'Sun' && p !== 'Moon').map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}></Typography> }
                ], 'rising', true)}

                {renderRow([
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.setting_planet} onChange={(e) => setFormValues(v => ({...v, setting_planet: e.target.value}))}>
                                {planets.filter(p => p !== 'Sun').map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Declination more than that of the Sun</Typography> }
                ], 'declination_more_than_sun')}

                {renderRow([
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Eclipse</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.eclipse_type} onChange={(e) => setFormValues(v => ({...v, eclipse_type: e.target.value}))}>
                                <MenuItem value="Solar">Solar</MenuItem>
                                <MenuItem value="Lunar">Lunar</MenuItem>
                            </Select>
                        </FormControl>
                    )},
                    { component: (
                        <FormControlLabel
                            sx={{ ml: 1 }}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={!!formValues.eclipse_track_transits}
                                    onChange={(e) => setFormValues(v => ({
                                        ...v,
                                        eclipse_track_transits: e.target.checked,
                                        // The scope follows the eclipse type the moment it is
                                        // switched on, so the checkbox alone does something
                                        // sensible without a second question having to be answered
                                        // first. Changing it afterwards is still the reader's call.
                                        eclipse_transit_scope: e.target.checked
                                            ? v.eclipse_transit_scope || v.eclipse_type
                                            : v.eclipse_transit_scope,
                                    }))}
                                />
                            }
                            label={<Typography sx={{ fontSize: '0.875rem' }}>Planetary Transits over Eclipse Degree</Typography>}
                        />
                    )},
                    { component: (
                        <FormControl size="small" sx={{ width: 110 }} disabled={!formValues.eclipse_track_transits}>
                            <Select
                                value={formValues.eclipse_transit_scope}
                                onChange={(e) => setFormValues(v => ({...v, eclipse_transit_scope: e.target.value}))}
                            >
                                <MenuItem value="Solar">Solar</MenuItem>
                                <MenuItem value="Lunar">Lunar</MenuItem>
                                <MenuItem value="Both">Both</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                ], 'solarOrLunarEclipse', false)}

                {renderRow([
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.conjunct_star_planet} onChange={(e) => setFormValues(v => ({...v, conjunct_star_planet: e.target.value}))}>
                                {planets.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Conjunct with Star</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.conjunct_star} onChange={(e) => setFormValues(v => ({...v, conjunct_star: e.target.value}))}>
                                {fixedStars.map(s => <MenuItem key={s.name} value={s.name}>{s.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )}
                ], 'conjunct_star')}
                </Box>

                <Popover
                    open={isPickerOpen}
                    anchorEl={pickerAnchorEl}
                    onClose={handleColorClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                >
                    <SketchPicker
                        color={colors[currentPicker]}
                        onChangeComplete={handleColorChange}
                    />
                </Popover>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'flex-start' }}>
                <Button onClick={onClose} variant="contained" color="error">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

TimeAndEventStrategyDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onRun: PropTypes.func.isRequired,
    /** A Command Bus prefill. Applied once per distinct object; null means "no prefill". */
    initialConfig: PropTypes.object,
};

export default TimeAndEventStrategyDialog;